// ══════════════════════════════════════════════════════════════════════════════
// SECURITY MODULE — Sykes Anaesthetics v14.6 Security Hardening
// Addresses: SEC-03, SEC-07, SEC-11, SEC-13, SEC-14, SEC-15
// ══════════════════════════════════════════════════════════════════════════════

// ── SEC-03: AES-256-GCM ENCRYPTION (WebCrypto API) ─────────────────────────
// Encrypts all localStorage values using a key derived from a passphrase
// via PBKDF2. The IV is prepended to each ciphertext.

const ENCRYPTION_ALGO = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // bytes
const SALT_LENGTH = 16; // bytes
const PBKDF2_ITERATIONS = 100000;

let _encryptionKey = null;
let _encryptionEnabled = false;

async function deriveKey(passphrase, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: ENCRYPTION_ALGO, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

export const Encryption = {
  /** Initialise encryption with a user-supplied passphrase */
  async init(passphrase) {
    try {
      if (!crypto?.subtle) {
        console.warn('[Security] WebCrypto not available — encryption disabled');
        return false;
      }
      // Use a fixed salt stored alongside the data (or generate on first use)
      let saltHex = localStorage.getItem('sykes-enc-salt');
      let salt;
      if (!saltHex) {
        salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
        saltHex = Array.from(salt).map(b => b.toString(16).padStart(2, '0')).join('');
        localStorage.setItem('sykes-enc-salt', saltHex);
      } else {
        salt = new Uint8Array(saltHex.match(/.{2}/g).map(h => parseInt(h, 16)));
      }
      _encryptionKey = await deriveKey(passphrase, salt);
      _encryptionEnabled = true;
      return true;
    } catch (e) {
      console.warn('[Security] Encryption init failed:', e.message);
      return false;
    }
  },

  isEnabled() { return _encryptionEnabled; },

  /** Encrypt a string → base64 ciphertext (IV prepended) */
  async encrypt(plaintext) {
    if (!_encryptionEnabled || !_encryptionKey) return plaintext;
    try {
      const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
      const encoded = new TextEncoder().encode(plaintext);
      const cipherBuffer = await crypto.subtle.encrypt(
        { name: ENCRYPTION_ALGO, iv },
        _encryptionKey,
        encoded
      );
      // Prepend IV to ciphertext
      const combined = new Uint8Array(IV_LENGTH + cipherBuffer.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(cipherBuffer), IV_LENGTH);
      // Base64 encode
      return 'ENC:' + btoa(String.fromCharCode(...combined));
    } catch (e) {
      console.warn('[Security] Encrypt error:', e.message);
      return plaintext;
    }
  },

  /** Decrypt a base64 ciphertext → plaintext string */
  async decrypt(ciphertext) {
    if (!_encryptionEnabled || !_encryptionKey) return ciphertext;
    if (!ciphertext || !ciphertext.startsWith('ENC:')) return ciphertext; // not encrypted
    try {
      const raw = atob(ciphertext.slice(4));
      const bytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
      const iv = bytes.slice(0, IV_LENGTH);
      const data = bytes.slice(IV_LENGTH);
      const decrypted = await crypto.subtle.decrypt(
        { name: ENCRYPTION_ALGO, iv },
        _encryptionKey,
        data
      );
      return new TextDecoder().decode(decrypted);
    } catch (e) {
      // FOLLOWUP-#8: don't warn here. Decrypt failures are nearly always
      // due to the JWT-derived passphrase having rotated since the data
      // was written (typical when previous-session data survives a
      // browser close without clean logout). Callers detect failure by
      // checking whether the return value still starts with 'ENC:' and
      // treat it as 'no data' — a genuine error (corruption, etc.) will
      // surface as data simply not loading, debuggable via other means.
      return ciphertext; // return as-is if decryption fails
    }
  }
};


// ── SEC-11: HTML SANITISATION ──────────────────────────────────────────────
// Escapes HTML entities in user-supplied strings before embedding in HTML exports.
// Prevents XSS in Blob-based document exports (Word/HTML).

const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
};

export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/[&<>"'/]/g, char => HTML_ENTITIES[char] || char);
}

/** Sanitise an object's string values recursively (for export) */
export function sanitiseForExport(obj) {
  if (typeof obj === 'string') return escapeHtml(obj);
  if (Array.isArray(obj)) return obj.map(sanitiseForExport);
  if (obj && typeof obj === 'object') {
    const result = {};
    for (const [k, v] of Object.entries(obj)) {
      result[k] = sanitiseForExport(v);
    }
    return result;
  }
  return obj;
}


// ── SEC-07: SESSION TIMEOUT / AUTO-LOGOUT ──────────────────────────────────
// Enforces automatic logout after configurable inactivity period.
// NHS DSPT standard: 15–30 minutes.
//
// Differentiated session timeouts (BACKLOG §9 Phase 1.6, 4 May 2026):
// admin gets longer (deep multi-step workflows like allocation),
// anaesthetist medium (active during sessions but PII-heavy if walked away),
// clinic shortest (often shared computers, regulatory baseline).
// Each portal imports the constant for its role and passes it to
// `new InactivityMonitor(callback, ms)` at construction.

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (legacy default; per-role constants below)
export const TIMEOUT_ADMIN_MS        = 60 * 60 * 1000; // 60 min
export const TIMEOUT_ANAESTHETIST_MS = 30 * 60 * 1000; // 30 min
export const TIMEOUT_CLINIC_MS       = 15 * 60 * 1000; // 15 min
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

export class InactivityMonitor {
  constructor(onTimeout, timeoutMs = TIMEOUT_MS) {
    this._onTimeout = onTimeout;
    this._timeoutMs = timeoutMs;
    this._timer = null;
    this._handler = this._resetTimer.bind(this);
    this._active = false;
    this._warningCallback = null;
    this._warningTimer = null;
    this._warningMs = 2 * 60 * 1000; // Warn 2 minutes before logout
  }

  start(warningCallback) {
    if (this._active) return;
    this._active = true;
    this._warningCallback = warningCallback || null;
    ACTIVITY_EVENTS.forEach(ev => document.addEventListener(ev, this._handler, { passive: true }));
    this._resetTimer();
  }

  stop() {
    this._active = false;
    ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, this._handler));
    if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    if (this._warningTimer) { clearTimeout(this._warningTimer); this._warningTimer = null; }
  }

  _resetTimer() {
    if (this._timer) clearTimeout(this._timer);
    if (this._warningTimer) clearTimeout(this._warningTimer);
    // Set warning timer
    if (this._warningCallback) {
      this._warningTimer = setTimeout(() => {
        this._warningCallback(Math.round(this._warningMs / 1000));
      }, this._timeoutMs - this._warningMs);
    }
    // Set logout timer
    this._timer = setTimeout(() => {
      this._active = false;
      ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, this._handler));
      this._onTimeout();
    }, this._timeoutMs);
  }

  /** Extend session (e.g., user clicks "Stay logged in") */
  extend() {
    if (this._active) this._resetTimer();
  }
}


// ── SEC-13: BRUTE FORCE PROTECTION (client-side progressive delay) ─────────
// Tracks failed login attempts and enforces progressive delays + lockout.

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 5 * 60 * 1000; // 5 minute lockout after max attempts
const DELAY_BASE_MS = 1000; // Doubles with each failure

// BUG-31 (9 May 2026): bucket key changed from portal-only to `${portal}:${username}`
// to mirror the server-side fix. Previously a single mistyped admin password
// would lock the whole admin portal for any user on the same browser, even
// other admin accounts. Now each (portal, username) pair has its own counter,
// so self-testing across users no longer cross-locks. Callers updated to
// pass username; legacy callers fall through to portal-only via the '_'
// fallback inside _key().
const _loginAttempts = new Map(); // `${portal}:${username}` → { count, lastAttempt, lockedUntil }

function _key(portal, username) {
  const u = (typeof username === 'string' && username) ? username.toLowerCase().trim() : '_';
  return `${portal}:${u}`;
}

export const BruteForceGuard = {
  /** Check if login is currently allowed for a (portal, username). Returns { allowed, waitMs, message } */
  check(portal, username) {
    const k = _key(portal, username);
    const state = _loginAttempts.get(k) || { count: 0, lastAttempt: 0, lockedUntil: 0 };
    const now = Date.now();
    if (state.lockedUntil > now) {
      const waitMs = state.lockedUntil - now;
      return { allowed: false, waitMs, message: `Account locked. Try again in ${Math.ceil(waitMs/1000)}s.` };
    }
    if (state.count > 0) {
      const delayMs = DELAY_BASE_MS * Math.pow(2, state.count - 1);
      const elapsed = now - state.lastAttempt;
      if (elapsed < delayMs) {
        return { allowed: false, waitMs: delayMs - elapsed, message: `Please wait ${Math.ceil((delayMs - elapsed)/1000)}s before retrying.` };
      }
    }
    return { allowed: true, waitMs: 0, message: '' };
  },

  /** Record a failed login attempt */
  recordFailure(portal, username) {
    const k = _key(portal, username);
    const state = _loginAttempts.get(k) || { count: 0, lastAttempt: 0, lockedUntil: 0 };
    state.count += 1;
    state.lastAttempt = Date.now();
    if (state.count >= MAX_ATTEMPTS) {
      state.lockedUntil = Date.now() + LOCKOUT_MS;
    }
    _loginAttempts.set(k, state);
    return state.count;
  },

  /** Reset on successful login */
  reset(portal, username) {
    _loginAttempts.delete(_key(portal, username));
  },

  /** Get remaining attempts */
  remaining(portal, username) {
    const state = _loginAttempts.get(_key(portal, username)) || { count: 0 };
    return Math.max(0, MAX_ATTEMPTS - state.count);
  }
};


// ── SEC-15: PRODUCTION ERROR HANDLING ──────────────────────────────────────
// Generic error messages in production; detailed in development.

const IS_PRODUCTION = typeof import.meta !== 'undefined' &&
  import.meta.env?.PROD === true;

export function safeErrorMessage(error, context = '') {
  if (!IS_PRODUCTION) {
    // Development: full detail
    return error?.message || String(error);
  }
  // Production: generic message, log internally
  console.error(`[${context}]`, error);
  return 'An error occurred. Please try again or contact support.';
}

export function safeConsoleError(context, error) {
  if (IS_PRODUCTION) {
    // In production, suppress stack traces to console
    console.error(`[${context}] Operation failed`);
  } else {
    console.error(`[${context}]`, error);
  }
}
